<template>
  <div class="short-instructions">
    <Heading
      v-if="step !== 'retargeting'"
      tag="h2"
      align="center"
      size="sm"
      spaces-bottom
    >
      Zwei Schritte. Kein Stress.<br />
      <template v-if="useFormal">
        So erhalten Sie
        <template v-if="isCashback">Ihr Cashback:</template>
        <template v-else>Ihre Gutschrift:</template>
      </template>
      <template v-else>
        So erhältst du <template v-if="isCashback">dein Cashback</template>
        <template v-else>deine Gutschrift</template> für App- und In-App-Käufe.
      </template>
    </Heading>

    <div class="short-instructions__steps">
      <Card
        v-bind="step1"
        class="short-instructions__step short-instructions__step--1"
      >
        <template #action>
          <template v-if="device === 'Desktop'">
            <LinkButton
              tag="button"
              type="button"
              primary
              label="Zahlungsmethode einrichten"
              @click="scrollTo('dcb-instructions')"
            />
          </template>
          <template v-else>
            <LinkButton
              tag="a"
              primary
              label="Zahlungsmethode einrichten"
              target="_blank"
              :href="link"
            />
          </template>
        </template>
      </Card>
      <span class="short-instructions__step-arrow"></span>
      <Card
        v-bind="step2"
        class="short-instructions__step short-instructions__step--2"
      >
        <template #action>
          <GetApp
            show-only-device-label
            link-google="https://play.google.com/store/apps/"
            link-apple="https://apps.apple.com/de/charts/iphone/top-paid-apps/36"
          />
        </template>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Heading,
  GetApp,
  Card,
  detectDevice,
  LinkButton,
} from '@i22team/td-longtail-vue-components';
import type { CardComponentProps } from '@i22team/td-longtail-vue-components';
import { googlePlay } from '~/components/dcb/qr-code-links';
import type { DcbStep } from '~/lib/dcb/dcb-double-opt-in';

export type ShortInstructionsProps = {
  step?: DcbStep;
  useFormal?: boolean;
  isCashback?: boolean;
};

const props = withDefaults(defineProps<ShortInstructionsProps>(), {
  useFormal: false,
  isCashback: false,
  step: undefined,
});

const device = detectDevice.useDetectDevice();

const link = computed(() => {
  if (device.value === 'iOS')
    return 'https://buy.itunes.apple.com/WebObjects/MZFinance.woa/wa/editAddress?mt=8&itscg=as10031&itsct=sms_setup';
  if (device.value === 'Android') return googlePlay;
  return undefined;
});

const step1: CardComponentProps = {
  heading: {
    tag: 'h3',
    text: 'Schritt 1',
    size: 'sm',
    spacesBottomRem: true,
  },
  text: props.useFormal
    ? 'Hinterlegen Sie "Handyrechnung" als oberste Zahlungsmethode auf ihrem Handy.'
    : 'Hinterlege „Handyrechnung“ als oberste Zahlungsmethode auf deinem Handy.',

  buttons: [
    {
      tag: 'a',
      href: '#dcb-instructions',
      primary: true,
      label: 'Zahlungsmethode einrichten',
    },
  ],
};

const step2: CardComponentProps = {
  heading: {
    tag: 'h3',
    text: 'Schritt 2',
    size: 'sm',
    spacesBottomRem: true,
  },
  text: props.useFormal
    ? `Im App-Store einkaufen oder In-App-Kauf tätigen, um ${props.isCashback ? 'Cashback' : 'Gutschrift'} zu erhalten.`
    : `App in deinem Store kaufen oder In-App-Kauf tätigen, um ${props.isCashback ? 'Cashback' : 'Gutschrift'} zu erhalten.`,
};

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.short-instructions {
  margin-bottom: $grid-base * 5;

  @include mq($mq-md) {
    margin-bottom: $grid-base * 8;
  }

  &__steps {
    margin-top: 2rem;
    margin-bottom: 2rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 3rem;
    @include mq($mq-md) {
      flex-direction: row;
    }
  }

  &__step {
    max-width: 400px;
  }

  &__step-arrow {
    width: 50px;
    height: 3px;
    position: relative;
    background-color: var(--color-primary);

    @include mq(false, $mq-md - 1) {
      transform: rotateZ(90deg);
    }

    &:before,
    &:after {
      content: '';
      position: absolute;
      display: block;
      right: 1px;
      width: 50%;
      height: 3px;
      background-color: var(--color-primary);
      transform-origin: right;
    }

    &:before {
      transform: rotateZ(-45deg);
    }

    &:after {
      transform: rotateZ(45deg);
    }
  }
}
</style>
